import React, { useState, useCallback, useEffect } from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage,
  faSpinner,
  faDownload,
  faTimes,
  faRotateRight,
  faPaperPlane,
  faLightbulb,
  faWandMagicSparkles,
  faCrop,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import socialApiClient from "../../cores/helpers/services/social-api-client";
import { rotateBlobWithCanvas } from "../../cores/helpers/rotate-image-blob";
import { fetchAiGeneratedImageBlob } from "../../cores/helpers/fetch-ai-generated-blob";
import { AiImageCropModal } from "../component/ai/AiImageCropModal";
import { AiImageViewerEditorModal } from "../component/ai/AiImageViewerEditorModal";
import {
  loadTextToImageRotations,
  saveTextToImageRotations,
} from "../../cores/helpers/text-to-image-rotation-storage";
import { useMeCore } from "../../cores/me";
import { useIntl } from "react-intl";
import { useRouterCore } from "../../cores/router";
import { buildPostImageFromBlobForSocial } from "../../cores/helpers/social-post-image-from-blob";
import { storePendingSocialPostImages } from "../../cores/helpers/pending-social-post-image";
import { Header2 } from "../layout/Header2";
import {
  AI_RESULTS_COLUMNS_STORAGE,
  loadAiResultsColumns,
  saveAiResultsColumns,
  type AiResultsColumns,
} from "../../cores/helpers/ai-results-grid-columns-storage";
import { storeAiEditorImportFromTextToImage } from "../../cores/helpers/ai-editor-import-from-text-to-image";

const GENERATE_OR_EDIT_ENDPOINT = "nanobanana/generate-or-edit";
const STATUS_ENDPOINT = "nanobanana/status";

interface GeneratedImage {
  id: string;
  url: string;
  prompt: string;
  timestamp: number;
}

const TEXT_TO_IMAGE_EMPTY_STEPS = [
  "app.textToImage.emptyStep1",
  "app.textToImage.emptyStep2",
  "app.textToImage.emptyStep3",
] as const;

export default function TextToImage() {
  const { companyId } = useMeCore();
  const intl = useIntl();
  const { goToSocialPostFromAiImage, goToAIGeneration } = useRouterCore();
  const [prompt, setPrompt] = useState("");
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [canCheckStatus, setCanCheckStatus] = useState(false);
  const [imageSize, setImageSize] = useState("4:5");
  /** Rotation in degrees (0, 90, 180, 270) per generated image id (persistido en localStorage) */
  const [imageRotations, setImageRotations] = useState<Record<string, number>>({});
  /** Evita podar rotaciones antes de que el historial haya cargado (lista vacía inicial). */
  const [historyReady, setHistoryReady] = useState(false);
  const [postingToSocialId, setPostingToSocialId] = useState<string | null>(null);
  const [resultColumns, setResultColumns] = useState<AiResultsColumns>(() =>
    loadAiResultsColumns(AI_RESULTS_COLUMNS_STORAGE.textToImage, 3)
  );
  const [cropModal, setCropModal] = useState<{
    imageId: string;
    src: string;
  } | null>(null);
  const [cropApplying, setCropApplying] = useState(false);
  const [cropPreparingId, setCropPreparingId] = useState<string | null>(null);
  const [viewerModal, setViewerModal] = useState<{
    imageId: string;
    src: string;
  } | null>(null);
  const [viewerApplying, setViewerApplying] = useState(false);
  const [viewerPreparingId, setViewerPreparingId] = useState<string | null>(null);

  useEffect(() => {
    saveAiResultsColumns(AI_RESULTS_COLUMNS_STORAGE.textToImage, resultColumns);
  }, [resultColumns]);

  useEffect(() => {
    if (!companyId) {
      setHistoryReady(false);
      return;
    }

    setImageRotations(loadTextToImageRotations(companyId));
    setHistoryReady(false);

    let cancelled = false;
    const loadHistory = async () => {
      try {
        const { data } = await socialApiClient().get("nanobanana/history", {
          params: { companyId, limit: 50 },
        });
        if (cancelled) return;
        const items = data?.payload || [];
        const mapped: GeneratedImage[] = items.map((item: any) => ({
          id: String(item.id),
          url: item.imageUrl,
          prompt: item.prompt || "",
          timestamp: new Date(item.createdAt).getTime() || Date.now(),
        }));
        setGeneratedImages(mapped);
      } catch {
        if (!cancelled) {
          setGeneratedImages([]);
        }
      } finally {
        if (!cancelled) {
          setHistoryReady(true);
        }
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  /** Quitar rotaciones de imágenes que ya no están en el historial */
  useEffect(() => {
    if (!companyId || !historyReady) return;
    setImageRotations((prev) => {
      const ids = new Set(generatedImages.map((g) => g.id));
      const next: Record<string, number> = {};
      let changed = false;
      for (const [id, deg] of Object.entries(prev)) {
        if (ids.has(id)) next[id] = deg;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [companyId, historyReady, generatedImages]);

  /** Persistir rotaciones al cambiar (por empresa) */
  useEffect(() => {
    if (!companyId) return;
    saveTextToImageRotations(companyId, imageRotations);
  }, [companyId, imageRotations]);

  const fetchTaskStatus = async (taskId: string) => {
    if (!companyId) {
      throw new Error(intl.formatMessage({ id: "app.aiGeneration.errNoCompanyId" }));
    }
    const { data } = await socialApiClient().get(STATUS_ENDPOINT, {
      params: { taskId, companyId },
    });
    const taskPayload = data?.payload || data;
    const resultUrl =
      taskPayload?.resultImageUrl ||
      taskPayload?.raw?.response?.resultImageUrl ||
      taskPayload?.raw?.data?.response?.resultImageUrl;

    if (taskPayload?.status === "success" && resultUrl) {
      return { imageUrl: resultUrl, taskPayload };
    }

    if (taskPayload?.raw?.errorMessage) {
      throw new Error(taskPayload.raw.errorMessage);
    }

    return { imageUrl: null, taskPayload };
  };

  const pollTaskStatus = async (taskId: string) => {
    const maxAttempts = 30;
    const delayMs = 2000;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      setGenerationProgress(70 + Math.round((attempt / maxAttempts) * 30));

      const { imageUrl } = await fetchTaskStatus(taskId);
      if (imageUrl) {
        return imageUrl;
      }

      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    throw new Error(intl.formatMessage({ id: "app.aiGeneration.errTimeout" }));
  };

  const handleGenerate = useCallback(async () => {
    if (!companyId) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errCompanyRequired" }));
      return;
    }
    if (!prompt.trim()) {
      setErrorMessage(intl.formatMessage({ id: "app.textToImage.errPromptRequired" }));
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setErrorMessage(null);
    setCanCheckStatus(false);
    setCurrentTaskId(null);

    try {
      setGenerationProgress(10);
      const formData = new FormData();
      formData.append("prompt", prompt.trim());
      formData.append("ecoMode", "false");
      formData.append("type", "TEXTTOIAMGE");
      // En UI dejamos solo "Tamaño"; la cantidad se fija en 1.
      formData.append("numImages", "1");
      formData.append("imageSize", imageSize);

      setGenerationProgress(50);
      const { data } = await socialApiClient().post(
        GENERATE_OR_EDIT_ENDPOINT,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: { companyId },
        }
      );

      setGenerationProgress(70);
      const providerCode = data?.payload?.raw?.code;
      const providerMessage = (data?.payload?.raw?.msg || "").toString();
      const insufficientCredits =
        providerCode === 402 ||
        providerMessage.toLowerCase().includes("insufficient");
      if (insufficientCredits) {
        throw new Error(intl.formatMessage({ id: "app.aiGeneration.errInsufficientCredits" }));
      }

      const taskId = data?.payload?.taskId || data?.data?.taskId || data?.taskId;
      if (!taskId) {
        throw new Error(intl.formatMessage({ id: "app.aiGeneration.errNoTaskId" }));
      }
      setCurrentTaskId(taskId);

      const imageUrl = await pollTaskStatus(taskId);

      setGenerationProgress(100);
      setCanCheckStatus(false);
      setCurrentTaskId(null);

      const generatedImage: GeneratedImage = {
        id: `${Date.now()}`,
        url: imageUrl,
        prompt: prompt.trim(),
        timestamp: Date.now(),
      };

      setGeneratedImages((prev) => [generatedImage, ...prev]);
    } catch (error: any) {
      console.error("TextToImage error:", error);

      const responseMessage = error?.response?.data?.message;
      const parsedResponseMessage = Array.isArray(responseMessage)
        ? responseMessage.join(" ")
        : responseMessage;
      const errorMsg =
        parsedResponseMessage ||
        error?.response?.data?.error?.message ||
        error?.message ||
        intl.formatMessage({ id: "app.aiGeneration.errUnknown" });
      setErrorMessage(errorMsg);

      const isTimeout =
        typeof errorMsg === "string" &&
        errorMsg.toLowerCase().includes("tiempo de espera agotado");
      if (isTimeout && currentTaskId) {
        setCanCheckStatus(true);
      } else {
        setCanCheckStatus(false);
        setCurrentTaskId(null);
      }
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  }, [companyId, prompt, imageSize, intl, currentTaskId]);

  const handleCheckStatus = async () => {
    if (!currentTaskId) return;
    setIsGenerating(true);
    setErrorMessage(null);
    try {
      const imageUrl = await pollTaskStatus(currentTaskId);
      const generatedImage: GeneratedImage = {
        id: `${Date.now()}`,
        url: imageUrl,
        prompt: prompt,
        timestamp: Date.now(),
      };
      setGeneratedImages((prev) => [generatedImage, ...prev]);
      setCanCheckStatus(false);
      setCurrentTaskId(null);
    } catch (error: any) {
      setErrorMessage(error.message || intl.formatMessage({ id: "app.aiGeneration.errCheckStatus" }));
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const rotateGeneratedImage = (id: string) => {
    setImageRotations((prev) => ({
      ...prev,
      [id]: ((prev[id] ?? 0) + 90) % 360,
    }));
  };

  const closeCropModal = () => {
    setCropModal((prev) => {
      if (prev?.src) URL.revokeObjectURL(prev.src);
      return null;
    });
  };

  const openCropModalForImage = async (img: GeneratedImage) => {
    setCropPreparingId(img.id);
    try {
      const raw = await fetchAiGeneratedImageBlob(img.url, companyId);
      const deg = imageRotations[img.id] ?? 0;
      const blob: Blob =
        deg !== 0 ? await rotateBlobWithCanvas(raw, deg) : raw;
      const src = URL.createObjectURL(blob);
      setCropModal({ imageId: img.id, src });
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.aiGeneration.cropOpenError" })
      );
    } finally {
      setCropPreparingId(null);
    }
  };

  const handleCropApply = async (blob: Blob) => {
    const modal = cropModal;
    if (!modal) return;
    setCropApplying(true);
    try {
      const newUrl = URL.createObjectURL(blob);
      setGeneratedImages((prev) =>
        prev.map((g) => {
          if (g.id !== modal.imageId) return g;
          if (g.url.startsWith("blob:")) URL.revokeObjectURL(g.url);
          return { ...g, url: newUrl };
        })
      );
      setImageRotations((prev) => {
        const next = { ...prev };
        delete next[modal.imageId];
        return next;
      });
      URL.revokeObjectURL(modal.src);
      setCropModal(null);
    } finally {
      setCropApplying(false);
    }
  };

  const closeViewerModal = () => {
    setViewerModal((prev) => {
      if (prev?.src) URL.revokeObjectURL(prev.src);
      return null;
    });
  };

  const openViewerForImage = async (img: GeneratedImage) => {
    setViewerPreparingId(img.id);
    try {
      const raw = await fetchAiGeneratedImageBlob(img.url, companyId);
      const deg = imageRotations[img.id] ?? 0;
      const blob: Blob =
        deg !== 0 ? await rotateBlobWithCanvas(raw, deg) : raw;
      const src = URL.createObjectURL(blob);
      setViewerModal({ imageId: img.id, src });
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.aiGeneration.cropOpenError" })
      );
    } finally {
      setViewerPreparingId(null);
    }
  };

  const handleViewerApply = async (blob: Blob) => {
    const modal = viewerModal;
    if (!modal) return;
    setViewerApplying(true);
    try {
      const newUrl = URL.createObjectURL(blob);
      setGeneratedImages((prev) =>
        prev.map((g) => {
          if (g.id !== modal.imageId) return g;
          if (g.url.startsWith("blob:")) URL.revokeObjectURL(g.url);
          return { ...g, url: newUrl };
        })
      );
      setImageRotations((prev) => {
        const next = { ...prev };
        delete next[modal.imageId];
        return next;
      });
      URL.revokeObjectURL(modal.src);
      setViewerModal(null);
    } finally {
      setViewerApplying(false);
    }
  };

  const downloadGeneratedImage = async (image: GeneratedImage) => {
    try {
      const data = await fetchAiGeneratedImageBlob(image.url, companyId);
      const deg = imageRotations[image.id] ?? 0;
      const blob: Blob = deg !== 0 ? await rotateBlobWithCanvas(data, deg) : data;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const datePart = new Date(image.timestamp).toISOString().slice(0, 10);
      link.download = `${datePart}_Wembii-${image.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errDownloadFailed" }));
    }
  };

  const openInAiEditor = useCallback(
    (image: GeneratedImage) => {
      if (!companyId) {
        toast.error(intl.formatMessage({ id: "app.aiGeneration.errCompanyRequired" }));
        return;
      }
      storeAiEditorImportFromTextToImage({
        imageUrl: image.url,
        prompt: image.prompt,
        rotationDeg: imageRotations[image.id] ?? 0,
      });
      void goToAIGeneration();
    },
    [companyId, goToAIGeneration, intl, imageRotations]
  );

  const postImageToSocial = async (image: GeneratedImage) => {
    if (!companyId) {
      toast.error(intl.formatMessage({ id: "app.aiGeneration.errCompanyRequired" }));
      return;
    }
    setPostingToSocialId(image.id);
    try {
      const data = await fetchAiGeneratedImageBlob(image.url, companyId);
      const deg = imageRotations[image.id] ?? 0;
      const blob: Blob =
        deg !== 0 ? await rotateBlobWithCanvas(data, deg) : data;
      const datePart = new Date(image.timestamp).toISOString().slice(0, 10);
      const postImage = await buildPostImageFromBlobForSocial(
        blob,
        `${datePart}_Wembii-${image.id}.jpg`
      );
      try {
        storePendingSocialPostImages([postImage]);
      } catch (e: any) {
        if (e?.name === "QuotaExceededError" || e?.code === 22) {
          toast.error(
            intl.formatMessage({ id: "app.aiGeneration.errPostToSocialStorage" })
          );
          return;
        }
        throw e;
      }
      goToSocialPostFromAiImage();
    } catch (e: any) {
      toast.error(
        intl.formatMessage({ id: "app.aiGeneration.errPostToSocialFailed" })
      );
    } finally {
      setPostingToSocialId(null);
    }
  };

  const removeGeneratedImage = (id: string) => {
    setGeneratedImages((prev) => {
      const found = prev.find((img) => img.id === id);
      if (found?.url.startsWith("blob:")) {
        URL.revokeObjectURL(found.url);
      }
      return prev.filter((img) => img.id !== id);
    });
    setImageRotations((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    socialApiClient()
      .delete(`nanobanana/history/${id}`, {
        params: { companyId },
      })
      .catch(() => {});
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      handleGenerate();
    }
  };

  return (
    <Layout>
      <Header2
        className="ai-module-header"
        title={intl.formatMessage({ id: "app.sidebar.textToImage" })}
      />
      <Content>
        <div className="ai-generation-container text-to-image-container" style={styles.container}>
          {/* Panel Izquierdo - Controles */}
          <div className="ai-generation-left-panel" style={styles.leftPanel}>
            <div style={styles.section}>
              <textarea
                className="ai-textarea"
                style={styles.textArea}
                placeholder={intl.formatMessage({ id: "app.textToImage.placeholder" })}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyPress}
                rows={5}
              />
              <div className="ai-left-muted" style={styles.shortcutHint}>
                <span className="ai-kbd" style={styles.shortcutKey}>
                  ⌘
                </span>
                <span style={styles.shortcutPlus}>+</span>
                <span className="ai-kbd" style={styles.shortcutKey}>
                  Enter
                </span>
              </div>
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionSubtitle}>
                {intl.formatMessage({ id: "app.textToImage.sectionOptionsTitle" })}
              </h4>
              <div style={styles.optionsRow}>
                <label style={styles.label}>
                  {intl.formatMessage({ id: "app.textToImage.imageSize" })}
                </label>
                <select
                  className="ai-select"
                  style={styles.selectInput}
                  value={imageSize}
                  onChange={(e) => setImageSize(e.target.value)}
                >
                  <option value="4:5">4:5</option>
                  <option value="1:1">1:1</option>
                  <option value="16:9">16:9</option>
                  <option value="9:16">9:16</option>
                </select>
              </div>
            </div>

            {errorMessage && (
              <div className="ai-gen-alert ai-gen-alert--error" style={styles.errorMessage}>
                {errorMessage}
              </div>
            )}

            {canCheckStatus && currentTaskId && !isGenerating && (
              <>
                <button
                  type="button"
                  className="ai-gen-secondary-btn"
                  style={styles.secondaryButton}
                  onClick={handleCheckStatus}
                >
                  {intl.formatMessage({ id: "app.aiGeneration.checkStatusBtn" })}
                </button>
                <div className="ai-left-muted" style={styles.helperText}>
                  {intl.formatMessage({ id: "app.aiGeneration.checkStatusHelper" })}
                </div>
              </>
            )}

            <button
              type="button"
              className="ai-gen-generate-btn"
              style={{
                ...styles.generateButton,
                ...(isGenerating || !prompt.trim()
                  ? styles.generateButtonDisabled
                  : {}),
              }}
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
            >
              {isGenerating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
                  {intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faImage} style={{ marginRight: "8px" }} />
                  {intl.formatMessage({ id: "app.textToImage.generateBtn" })}
                </>
              )}
            </button>
          </div>

          {/* Panel Derecho - Resultados */}
          <div
            className={cn(
              "ai-generation-right-panel",
              generatedImages.length === 0 &&
                !isGenerating &&
                "ai-generation-right-panel--text-to-image-empty"
            )}
            style={styles.rightPanel}
          >
            {generatedImages.length > 0 ? (
              <>
                <div className="ai-results-toolbar">
                  <label
                    htmlFor="ai-results-cols-tti"
                    className="ai-results-toolbar__label"
                  >
                    {intl.formatMessage({
                      id: "app.aiGeneration.resultsGridColumnsLabel",
                    })}
                  </label>
                  <select
                    id="ai-results-cols-tti"
                    className="ai-select ai-results-toolbar__select"
                    value={resultColumns}
                    onChange={(e) =>
                      setResultColumns(
                        Number(e.target.value) as AiResultsColumns
                      )
                    }
                    aria-label={intl.formatMessage({
                      id: "app.aiGeneration.resultsGridColumnsAria",
                    })}
                  >
                    {([1, 2, 3, 4] as const).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  className={`ai-results-grid ai-results-grid--cols-${resultColumns}`}
                >
                {isGenerating && (
                  <div className="ai-loading-card" style={styles.loadingCard}>
                    <div style={styles.loadingSpinner}>
                      <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    </div>
                    <p style={styles.loadingText}>
                      {intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}
                    </p>
                    {generationProgress > 0 && (
                      <div style={styles.progressBar}>
                        <div
                          style={{
                            ...styles.progressFill,
                            width: `${generationProgress}%`,
                          }}
                        />
                      </div>
                    )}
                    <p style={styles.progressText}>{generationProgress}%</p>
                  </div>
                )}

                {generatedImages.map((img) => {
                  const deg = imageRotations[img.id] ?? 0;
                  return (
                  <div key={img.id} className="ai-result-card" style={styles.resultCard}>
                    <div
                      className="ai-result-image-wrap"
                      style={styles.resultImageWrapper}
                    >
                      <img
                        src={img.url}
                        alt="Imagen generada"
                        className="ai-result-image"
                        style={{
                          ...styles.resultImage,
                          transform: `rotate(${deg}deg)`,
                          transition: "transform 0.25s ease",
                        }}
                        onError={(e) => {
                          e.currentTarget.src = `https://picsum.photos/512/512?random=${Date.now()}`;
                        }}
                      />
                    </div>
                    <div className="ai-result-prompt" style={styles.resultPrompt}>
                      {img.prompt}
                    </div>
                    <div className="ai-result-actions" style={styles.resultActions}>
                      <button
                        className="ai-result-action-btn"
                        style={styles.resultActionButton}
                        type="button"
                        onClick={() => openViewerForImage(img)}
                        disabled={viewerPreparingId === img.id || viewerApplying}
                        title={intl.formatMessage({
                          id: "app.aiGeneration.viewerOpenBtnTitle",
                        })}
                      >
                        <FontAwesomeIcon
                          icon={viewerPreparingId === img.id ? faSpinner : faEye}
                          spin={viewerPreparingId === img.id}
                        />{" "}
                        {intl.formatMessage({ id: "app.aiGeneration.viewerOpenBtn" })}
                      </button>
                      <button
                        className="ai-result-action-btn"
                        style={styles.resultActionButton}
                        type="button"
                        onClick={() => rotateGeneratedImage(img.id)}
                      >
                        <FontAwesomeIcon icon={faRotateRight} />{" "}
                        {intl.formatMessage({ id: "app.aiGeneration.rotateBtn" })}
                      </button>
                      <button
                        className="ai-result-action-btn"
                        style={styles.resultActionButton}
                        type="button"
                        onClick={() => openCropModalForImage(img)}
                        disabled={cropPreparingId === img.id || cropApplying}
                      >
                        <FontAwesomeIcon
                          icon={cropPreparingId === img.id ? faSpinner : faCrop}
                          spin={cropPreparingId === img.id}
                        />{" "}
                        {intl.formatMessage({ id: "app.aiGeneration.cropBtn" })}
                      </button>
                      <button
                        className="ai-result-action-btn"
                        style={styles.resultActionButton}
                        type="button"
                        onClick={() => downloadGeneratedImage(img)}
                      >
                        <FontAwesomeIcon icon={faDownload} />{" "}
                        {intl.formatMessage({ id: "app.aiGeneration.downloadBtn" })}
                      </button>
                      <button
                        className="ai-result-action-danger-btn"
                        style={styles.resultActionDangerButton}
                        type="button"
                        onClick={() => removeGeneratedImage(img.id)}
                      >
                        <FontAwesomeIcon icon={faTimes} />{" "}
                        {intl.formatMessage({ id: "app.aiGeneration.deleteBtn" })}
                      </button>
                    </div>
                    <div className="ai-result-post-wrap">
                      <button
                        type="button"
                        className="ai-result-edit-in-editor-btn"
                        onClick={() => openInAiEditor(img)}
                        title={intl.formatMessage({
                          id: "app.textToImage.editInEditorBtnTitle",
                        })}
                      >
                        <FontAwesomeIcon icon={faWandMagicSparkles} />{" "}
                        <span>
                          {intl.formatMessage({
                            id: "app.textToImage.editInEditorBtn",
                          })}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="ai-result-post-btn"
                        disabled={postingToSocialId === img.id}
                        onClick={() => postImageToSocial(img)}
                      >
                        {postingToSocialId === img.id ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />{" "}
                            <span>
                              {intl.formatMessage({
                                id: "app.aiGeneration.postToSocialLoading",
                              })}
                            </span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPaperPlane} />{" "}
                            <span>
                              {intl.formatMessage({
                                id: "app.aiGeneration.postToSocialBtn",
                              })}
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
                })}
                </div>
              </>
            ) : isGenerating ? (
              <div style={styles.loadingContainer}>
                <div style={styles.loadingSpinner}>
                  <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                </div>
                <p style={styles.loadingText}>
                  {intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}
                </p>
                {generationProgress > 0 && (
                  <div style={styles.progressBar}>
                    <div
                      style={{
                        ...styles.progressFill,
                        width: `${generationProgress}%`,
                      }}
                    />
                  </div>
                )}
                <p style={styles.progressText}>{generationProgress}%</p>
              </div>
            ) : (
              <div className="text-to-image-empty">
                <div className="text-to-image-empty__icon-wrap" aria-hidden>
                  <FontAwesomeIcon icon={faImage} size="2x" />
                </div>
                <h2 className="text-to-image-empty__title">
                  {intl.formatMessage({ id: "app.textToImage.emptyTitle" })}
                </h2>
                <p className="text-to-image-empty__intro">
                  {intl.formatMessage({ id: "app.textToImage.emptyIntro" })}
                </p>
                <ol className="text-to-image-empty__steps">
                  {TEXT_TO_IMAGE_EMPTY_STEPS.map((stepId, index) => (
                    <li key={stepId} className="text-to-image-empty__step">
                      <span className="text-to-image-empty__step-num">
                        {index + 1}
                      </span>
                      <span>{intl.formatMessage({ id: stepId })}</span>
                    </li>
                  ))}
                </ol>
                <p className="text-to-image-empty__tip" role="note">
                  <FontAwesomeIcon
                    icon={faLightbulb}
                    className="text-to-image-empty__tip-icon"
                    aria-hidden
                  />
                  {intl.formatMessage({ id: "app.textToImage.emptyTip" })}
                </p>
              </div>
            )}
          </div>
        </div>
      </Content>
      <AiImageCropModal
        isOpen={!!cropModal}
        imageSrc={cropModal?.src ?? null}
        onClose={closeCropModal}
        onApply={handleCropApply}
        busy={cropApplying}
      />
      <AiImageViewerEditorModal
        isOpen={!!viewerModal}
        imageSrc={viewerModal?.src ?? null}
        onClose={closeViewerModal}
        onApply={handleViewerApply}
        busy={viewerApplying}
      />
    </Layout>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    height: "calc(100vh - 110px)",
    gap: "20px",
    padding: "16px 20px",
    color: "#41463d",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  leftPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    overflowY: "auto",
    paddingRight: "12px",
  },
  rightPanel: {
    flex: 1,
    minWidth: 0,
    borderRadius: "30px",
    padding: "24px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    border: "3px solid #e6e6ee",
  },
  section: {
    marginBottom: "8px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "8px",
    color: "#0B65C2",
  },
  sectionSubtitle: {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "12px",
    color: "#41463d",
  },
  textArea: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "15px",
    padding: "16px",
    color: "#41463d",
    fontSize: "14px",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: "140px",
  },
  optionsRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  label: {
    fontSize: "13px",
    color: "#41463d",
    minWidth: "80px",
  },
  selectInput: {
    flex: 1,
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "10px",
    padding: "8px 10px",
    color: "#41463d",
    fontSize: "12px",
    fontFamily: "inherit",
  },
  shortcutHint: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "8px",
    justifyContent: "flex-end",
    fontSize: "11px",
    color: "#6c757d",
  },
  shortcutKey: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e6e6ee",
    borderRadius: "4px",
    padding: "2px 6px",
    fontSize: "10px",
    color: "#41463d",
  },
  shortcutPlus: {
    color: "#6c757d",
  },
  errorMessage: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "15px",
    padding: "12px",
    color: "#856404",
    fontSize: "14px",
    marginBottom: "8px",
  },
  secondaryButton: {
    width: "100%",
    backgroundColor: "#ffffff",
    color: "#0B65C2",
    border: "1px solid #0B65C2",
    borderRadius: "25px",
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: "8px",
  },
  helperText: {
    fontSize: "11px",
    color: "#6c757d",
    textAlign: "center",
    marginTop: "4px",
    marginBottom: "4px",
  },
  generateButton: {
    width: "100%",
    backgroundColor: "#0B65C2",
    color: "#ffffff",
    border: "none",
    borderRadius: "25px",
    padding: "16px 24px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    marginTop: "24px",
  },
  generateButtonDisabled: {
    backgroundColor: "#c4c4c4",
    color: "#6c757d",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    width: "100%",
    minHeight: "300px",
    alignSelf: "stretch",
  },
  loadingSpinner: {
    color: "#0B65C2",
  },
  loadingText: {
    fontSize: "18px",
    color: "#41463d",
    fontWeight: 500,
  },
  progressBar: {
    width: "100%",
    maxWidth: "300px",
    height: "4px",
    backgroundColor: "#e6e6ee",
    borderRadius: "2px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#0B65C2",
    transition: "width 0.3s ease",
  },
  progressText: {
    fontSize: "14px",
    color: "#6c757d",
    fontWeight: 500,
  },
  loadingCard: {
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    border: "2px dashed #0B65C2",
    padding: "16px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  resultCard: {
    borderRadius: "20px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    minWidth: 0,
    maxWidth: "100%",
  },
  resultImageWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    padding: "0 4px",
    boxSizing: "border-box",
  },
  resultImage: {
    maxWidth: "100%",
    width: "100%",
    height: "auto",
    maxHeight: "min(85vh, 1200px)",
    objectFit: "contain",
    display: "block",
  },
  resultPrompt: {
    padding: "12px",
    fontSize: "12px",
    minHeight: "48px",
  },
  resultActions: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    alignItems: "stretch",
    gap: "8px",
    padding: "10px 12px 12px",
  },
  resultActionButton: {
    flex: "none",
    width: "100%",
    borderRadius: "10px",
    padding: "10px 10px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    boxSizing: "border-box",
  },
  resultActionDangerButton: {
    flex: "none",
    width: "100%",
    borderRadius: "10px",
    padding: "10px 10px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    boxSizing: "border-box",
  },
  promptBar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    maxWidth: "600px",
    backgroundColor: "#f8f9fa",
    border: "1px solid #e6e6ee",
    borderRadius: "24px",
    padding: "12px 16px",
  },
  promptInput: {
    flex: 1,
    border: "none",
    backgroundColor: "transparent",
    fontSize: "15px",
    fontWeight: 400,
    outline: "none",
    minHeight: "24px",
    padding: "0",
  },
  submitButton: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    backgroundColor: "#0B65C2",
    color: "#ffffff",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  submitButtonDisabled: {
    backgroundColor: "#c4c4c4",
    cursor: "not-allowed",
    opacity: 0.6,
  },
};
